// components/entities/pages/LeadPage.tsx
//
// A lead is one brand judged against one property. The page is the brand's shortlist detail, with
// the scored analysis injected as its Scoring tab, exactly like a unit fit (UnitFitPage) so the same
// brand looks the same wherever it is opened.

import { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import type {
  AnalysisViewMode,
  LeadInventoryEntityState,
  PortfolioFitDTO,
} from '#types/types';
import {
  buildCriterionGroups,
  mergeAtomicFacts,
  mergeCriteriaResults,
} from '#lib/leasing/analysisGroups';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntitySkeleton, EntityView } from '@/components/entities/EntityView';
import { ScoredAnalysis, type ScoredAnalysisRunState } from '@/components/entities/ScoredAnalysis';
import { BrandShortlistDetail } from '@/components/portal/BrandShortlistDetail';
import { readCampaignRow } from '#lib/leasing/campaignRow';
import { isScored } from '#lib/leasing/leadStatus';

interface LeadPageProps {
  leadId: string;
  className?: string;
  lockedUnitId?: string;
  hasEntityRowAbove?: boolean;
}

const PENDING_VERDICT_LABEL = { en: 'Pending verdict', fr: 'Verdict en attente' };

export const LeadPage = ({ leadId, className, lockedUnitId, hasEntityRowAbove = false }: LeadPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);

  const lead = useKernelStore(
    (state) => (state.inventory.Lead.registry[leadId] as LeadInventoryEntityState | undefined) ?? null
  );
  const portfolioFit = useKernelStore((state) => {
    const portfolioFitId = lead?.portfolioFitId;
    if (!portfolioFitId) return null;
    return (state.inventory.PortfolioFit.registry[portfolioFitId] as PortfolioFitDTO | undefined) ?? null;
  });
  const parentCampaignStatus = useKernelStore((state) => {
    if (!lead?.lastCampaignId) return null;
    const campaign = readCampaignRow(state.inventory, lead.lastCampaignId);
    return typeof campaign?.status === 'string' ? campaign.status.toUpperCase() : null;
  });

  useEffect(() => {
    if (kernel.current && leadId && !lead) {
      void kernel.current.syncEntity('Lead', leadId);
    }
  }, [kernel, leadId, lead]);

  useEffect(() => {
    const portfolioFitId = lead?.portfolioFitId;
    if (!kernel.current || !portfolioFitId || portfolioFit) return;
    void kernel.current.syncEntity('PortfolioFit', portfolioFitId);
  }, [kernel, lead?.portfolioFitId, portfolioFit]);

  const setAnalysisViewMode = useCallback(
    (analysisViewMode: AnalysisViewMode) => {
      kernel.current?.patchEntityRuntimeState('Lead', leadId, { analysisViewMode });
    },
    [kernel, leadId]
  );

  const groups = useMemo(
    () =>
      buildCriterionGroups({
        // A lead inherits the portfolio-level verdict it was cut from, and shows both as one table.
        criteria: mergeCriteriaResults(lead?.criteriaResults, portfolioFit?.criteriaResults),
        strategy: lead?.strategy,
        rawFacts: mergeAtomicFacts(lead?.rawFacts, portfolioFit?.rawFacts),
        pendingLabel: PENDING_VERDICT_LABEL[lang] ?? PENDING_VERDICT_LABEL.en,
      }),
    [lead?.criteriaResults, lead?.strategy, lead?.rawFacts, portfolioFit?.criteriaResults, portfolioFit?.rawFacts, lang]
  );

  if (!lead) {
    return <EntitySkeleton variant="detail" className={className} />;
  }

  const normalizedStatus = String(lead.status || '').toUpperCase();
  const effectiveStatus =
    (normalizedStatus === 'RUNNING' || normalizedStatus === 'PENDING') &&
    (parentCampaignStatus === 'ABORTED' || parentCampaignStatus === 'FAILED')
      ? 'ABORTED'
      : normalizedStatus;
  const runState: ScoredAnalysisRunState =
    effectiveStatus === 'RUNNING'
      ? 'running'
      : effectiveStatus === 'PENDING'
        ? 'pending'
        : effectiveStatus === 'ABORTED'
          ? 'aborted'
          : null;

  // An unscored row still holds its previous run's verdict: never shown as this run's (2026-09-26).
  const scoredNow = isScored(effectiveStatus);
  const rationale = !scoredNow ? '' :
    (typeof lead.rationale === 'string' && lead.rationale.trim()) ||
    (typeof portfolioFit?.rationale === 'string' && portfolioFit.rationale.trim()) ||
    '';

  return (
    <div
      className={clsx('overflow-hidden', 'bg-black', className)}
    >
      {!hasEntityRowAbove && (
        <EntityView
          type="Lead"
          entityId={leadId}
          variant="row"
          leadLockedUnitId={lockedUnitId}
          className="!cursor-default"
        />
      )}

      <div className="px-3 pb-3 pt-3 animate-in fade-in duration-300">
        <BrandShortlistDetail
          brandId={lead.brandId ?? ''}
          lang={lang}
          summary={rationale || null}
          // No verdict and no run in flight means no analysis at all: the Scoring tab is dropped
          // rather than opened onto an empty table.
          scoring={
            (scoredNow && groups.length > 0) || runState ? (
              <ScoredAnalysis
                groups={scoredNow ? groups : []}
                runState={runState}
                viewMode={lead.analysisViewMode ?? lead.runtime?.analysisViewMode ?? 'simple'}
                onViewModeChange={setAnalysisViewMode}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
};
